import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ProcessTable from "../components/ProcessesTable";
import { useEffect, useRef, useState } from "react";
import VisualizacaoToggle from "../components/VisualizacaoToggle";
import CardViewProcesses from "../components/CardViewProcesses";
import { SearchProcessModal } from "../components/SearchProcessModal";
import LayoutFixHome from "../components/LayoutFixHome";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import ClientCardProcess from "../components/ClientCardProcess";
import { RoutesConfig } from "../router/types";
import { useNavigate } from "react-router-dom";
import { getClientProcess } from "../services/clientService";
import { toast } from "react-toastify";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useUserStore } from "../store/userStore";
import { useProcessStore } from "../store/useProcessStore";

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { route, user } = useAuthenticator((context) => [context.route]);
  const navigate = useNavigate();
  const [visualizacao, setVisualizacao] = useState<"card" | "list">(
    isMobile ? "card" : "list"
  );
  const [showProcessSearchModal, setProcessSearchModal] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
  const { reloadFlag } = useProcessStore();
  const cnpj = useUserStore((state) => state.cnpj);
  const profile = useUserStore((state) => state.profile);

  const [localPicture, setLocalPicture] = useState<string | null>(null);
  const [name, setName] = useState<string>("");

  const fetchUserData = async () => {
    try {
      const [attributes, session] = await Promise.all([
        fetchUserAttributes(),
        fetchAuthSession(),
      ]);

      const state = useUserStore.getState();

      const userData = {
        ...state,
        picture: attributes?.picture || "",
        clientName: attributes?.name || "",
        cnpj: attributes?.["custom:cnpj"] || "36977086000154",
        profile: session.tokens?.idToken?.payload?.["cognito:groups"] || [],
        email: attributes?.email || "",
        idToken: session.tokens?.idToken?.toString() ?? "",
      };

      useUserStore.getState().setUserData(userData);

      setLocalPicture(userData.picture);
      setName(userData.clientName);
      setIsUserDataLoaded(true);
    } catch (error) {
      console.error("Erro ao buscar dados do usuário:", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (route === "authenticated" && isUserDataLoaded) {
      if (name === "") {
        navigate(RoutesConfig.REGISTER.path);
      } else {
        navigate(RoutesConfig.HOME.path);
      }
    }
  }, [route, name, isUserDataLoaded, navigate]);

  const handleSearchProcess = () => {
    setProcessSearchModal(true);
  };
  const [resultClientProcesses, setResultClientProcesses] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const getClientProcesses = async () => {
    if (isLoading || !hasMore || !cnpj || cnpj.trim() === "") return;

    setIsLoading(true);
    try {
      const data = await getClientProcess(cnpj, nextCursor);

      setResultClientProcesses((prevProcesses: any) => {
        const newClientProcesses = data.items.filter(
          (newClient: any) =>
            !prevProcesses.some(
              (existing: any) => existing.CD_PROCESSO === newClient.CD_PROCESSO
            )
        );
        return [...prevProcesses, ...newClientProcesses];
      });

      setNextCursor(data.nextCursor);
      setHasMore(data.hasNextPage);
    } catch (err) {
      toast.error("Erro ao buscar processos do cliente");
      console.error("Erro ao buscar processos do cliente:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (profile[0] === "cliente" && cnpj && cnpj.trim() !== "") {
      getClientProcesses();
    }
  }, [profile, cnpj]);

  useEffect(() => {
    if (
      resultClientProcesses &&
      resultClientProcesses.length > 0 &&
      isFirstLoad
    ) {
      setIsFirstLoad(false);
    }
  }, [resultClientProcesses]);

  const handleLoadMore = () => {
    if (hasMore && nextCursor) {
      getClientProcesses();
    }
  };

  const reloadClientProcesses = async () => {
    setResultClientProcesses([]);
    setNextCursor(null);
    setHasMore(true);
    setIsLoading(true);
    try {
      const data = await getClientProcess(cnpj, null);

      if (data?.items?.length) {
        setResultClientProcesses(data.items);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasNextPage);
      } else {
        setResultClientProcesses([]);
        setHasMore(false);
      }
    } catch (err) {
      toast.error("Erro ao recarregar processos");
      console.error("Erro ao recarregar processos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (profile[0] === "cliente" && cnpj && cnpj.trim() !== "") {
      reloadClientProcesses();
    }
  }, [reloadFlag]);

  const autoLoadCount = useRef(0);

  useEffect(() => {
    if (profile[0] !== "cliente") return;

    const checkContentHeight = () => {
      const contentHeight = document.body.scrollHeight;
      const windowHeight = window.innerHeight;

      if (
        hasMore &&
        contentHeight <= windowHeight &&
        autoLoadCount.current < 4
      ) {
        autoLoadCount.current += 1;
        handleLoadMore();
      }
    };

    const timeout = setTimeout(checkContentHeight, 200);
    return () => clearTimeout(timeout);
  }, [resultClientProcesses.length, hasMore]);

  return (
    <>
      <LayoutFixHome>
        {profile[0] === "cliente" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "45px",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  marginTop: {
                    sm: "150px",
                    xs: "150px",
                    md: 0,
                  },
                }}
              >
                <InsertDriveFileIcon
                  sx={{ color: "#0A0C33", height: "30px", width: "27px" }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#0A0C33" }}
                >
                  Meus processos
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginTop: {
                    sm: "150px",
                    xs: "150px",
                    md: 0,
                  },
                }}
              >
                {!isMobile && <VisualizacaoToggle onChange={setVisualizacao} />}
                <Button
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#326AFD",
                    color: "#FFFFFF",
                    borderRadius: "27px",
                    fontSize: "16px",
                    fontWeight: "700",
                    width: "202px",
                  }}
                  onClick={() => setProcessSearchModal(true)}
                >
                  Buscar Processo
                </Button>
              </Box>
            </Box>
            {resultClientProcesses.length > 0 &&
              (visualizacao === "card" ? (
                <CardViewProcesses
                  list={resultClientProcesses}
                  loadMoreData={handleLoadMore}
                  isLoading={isLoading}
                  reloadData={reloadClientProcesses}
                  hasMore={hasMore}
                />
              ) : (
                <ProcessTable
                  list={resultClientProcesses}
                  loadMoreData={handleLoadMore}
                  isLoading={isLoading}
                  reloadData={reloadClientProcesses}
                />
              ))}
            {/* {isFirstLoad && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding={4}
              >
                <CircularProgress
                  size={48}
                  thickness={4}
                  style={{ color: "#326AFD" }}
                />
              </Box>
            )} */}

            {isLoading && resultClientProcesses.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding={4}
              >
                <CircularProgress
                  size={48}
                  thickness={4}
                  style={{ color: "#326AFD" }}
                />
              </Box>
            )}

            <SearchProcessModal
              open={showProcessSearchModal}
              onClose={() => setProcessSearchModal(false)}
            />
          </>
        )}
        {profile[0] === "analista" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "29px",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  marginTop: {
                    sm: "75px",
                    xs: "75px",
                    md: 0,
                  },
                }}
              >
                <InsertDriveFileIcon
                  sx={{ color: "#0A0C33", height: "30px", width: "27px" }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#0A0C33" }}
                >
                  Processos
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginTop: {
                    sm: "75px",
                    xs: "75px",
                    md: 0,
                  },
                }}
              >
                <Button
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#326AFD",
                    color: "#FFFFFF",
                    borderRadius: "27px",
                    fontSize: "16px",
                    fontWeight: "700",
                    width: "202px",
                  }}
                  onClick={() => navigate(RoutesConfig.MEUSPROCESSOS.path)}
                >
                  <InsertLinkIcon
                    sx={{ transform: "rotate(45deg)", marginRight: "5px" }}
                  />
                  Meus Processos
                </Button>
              </Box>
            </Box>
            <ClientCardProcess />
            <SearchProcessModal
              open={showProcessSearchModal}
              onClose={() => setProcessSearchModal(false)}
            />
          </>
        )}
        {profile[0] === "admin" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  marginTop: {
                    sm: "75px",
                    xs: "75px",
                    md: 0,
                  },
                }}
              >
                <InsertDriveFileIcon
                  sx={{ color: "#0A0C33", height: "30px", width: "27px" }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#0A0C33" }}
                >
                  Processos
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "column", md: "row" },
                  alignItems: { xs: "stretch", md: "center" },
                  gap: 2,
                  marginTop: {
                    sm: "75px",
                    xs: "75px",
                    md: 0,
                  },
                }}
              >
                {
                  <Button
                    sx={{
                      textTransform: "none",
                      color: "#326AFD",
                      borderRadius: "27px",
                      fontSize: "16px",
                      fontWeight: "700",
                      width: "202px",
                      border: "1px solid #326AFD",
                    }}
                    onClick={() => setProcessSearchModal(true)}
                  >
                    Buscar Processo
                  </Button>
                }
                <Button
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#326AFD",
                    color: "#FFFFFF",
                    borderRadius: "27px",
                    fontSize: "16px",
                    fontWeight: "700",
                    width: "202px",
                  }}
                  onClick={() => navigate(RoutesConfig.MEUSPROCESSOS.path)}
                >
                  <InsertLinkIcon
                    sx={{ transform: "rotate(45deg)", marginRight: "5px" }}
                  />
                  Meus Processos
                </Button>
              </Box>
            </Box>
            <ClientCardProcess />
            <SearchProcessModal
              open={showProcessSearchModal}
              onClose={() => setProcessSearchModal(false)}
            />
          </>
        )}
      </LayoutFixHome>
    </>
  );
};

export default Home;
